import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, ChevronDown, Menu, X, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import riekelsLogo from "@/assets/riekels-logo.png";
import { AnnouncementBar } from "./AnnouncementBar";

const services = [
  { name: "Lawn Care", path: "/services/lawn-care" },
  { name: "Aeration & Overseeding", path: "/services/aeration" },
  { name: "Mulching", path: "/services/mulching" },
  { name: "Dethatching", path: "/services/dethatching" },
  { name: "Spring & Fall Cleanups", path: "/services/spring-fall-cleanups" },
  { name: "Snow Removal", path: "/services/snow-removal" },
];

const aboutLinks = [
  { name: "Our Story", path: "/about" },
  { name: "Service Areas", path: "/service-areas" },
];

const ourWork = [
  { name: "Projects", path: "/projects" },
];

export const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [workOpen, setWorkOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileAboutOpen, setMobileAboutOpen] = useState(false);
  const [mobileWorkOpen, setMobileWorkOpen] = useState(false);
  const [showAnnouncement, setShowAnnouncement] = useState(true);
  const location = useLocation();

  // Hide announcement bar on scroll
  useEffect(() => {
    const handleScroll = () => {
      setShowAnnouncement(window.scrollY < 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent, action: () => void) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      action();
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      {/* Announcement Bar - slides away on scroll */}
      <div className={`transition-all duration-300 ${showAnnouncement ? 'max-h-12 opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}>
        <AnnouncementBar />
      </div>
      
      <div className="bg-background/95 backdrop-blur-sm border-b border-border">
        {/* Mobile Call Now Bar */}
        <div className="lg:hidden bg-secondary py-2">
          <a
            href="tel:+17632457813"
            className="flex items-center justify-center gap-2 text-secondary-foreground font-bold text-sm"
            aria-label="Call Riekels Lawn Care at (763) 245-7813"
          >
            <Phone className="w-4 h-4" aria-hidden="true" />
            Call Now: (763) 245-7813
          </a>
        </div>
        
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between py-2 lg:py-3">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0" aria-label="Riekels Lawn Care & Landscaping - Home">
              <img 
                src={riekelsLogo} 
                alt="Riekels Lawn Care & Landscaping logo" 
                className="h-16 lg:h-20 w-auto"
                width={160}
                height={80}
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8" aria-label="Main navigation">
              {/* Services Dropdown */}
              <div className="relative group">
                <button
                  className="flex items-center gap-1 text-foreground hover:text-secondary transition-colors font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2 rounded-sm"
                  onMouseEnter={() => setServicesOpen(true)}
                  onMouseLeave={() => setServicesOpen(false)}
                  onFocus={() => setServicesOpen(true)}
                  onBlur={(e) => {
                    if (!e.currentTarget.parentElement?.contains(e.relatedTarget)) {
                      setServicesOpen(false);
                    }
                  }}
                  aria-expanded={servicesOpen}
                  aria-haspopup="true"
                  aria-controls="services-menu"
                >
                  Services <ChevronDown className="w-4 h-4" aria-hidden="true" />
                </button>
                <div
                  id="services-menu"
                  className={`absolute top-full left-0 pt-2 ${servicesOpen ? "block" : "hidden"}`}
                  onMouseEnter={() => setServicesOpen(true)}
                  onMouseLeave={() => setServicesOpen(false)}
                  role="menu"
                  aria-label="Services submenu"
                >
                  <div className="bg-card rounded-xl shadow-xl border border-border py-2 min-w-[260px]">
                    {services.map((service) => (
                      <Link
                        key={service.path}
                        to={service.path}
                        className="block px-4 py-2.5 text-foreground hover:bg-muted hover:text-secondary transition-colors focus:outline-none focus-visible:bg-muted focus-visible:text-secondary"
                        role="menuitem"
                        onBlur={(e) => {
                          if (!e.currentTarget.parentElement?.contains(e.relatedTarget)) {
                            setServicesOpen(false);
                          }
                        }}
                      >
                        {service.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

              {/* Our Work Dropdown */}
              <div className="relative group">
                <button
                  className="flex items-center gap-1 text-foreground hover:text-secondary transition-colors font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2 rounded-sm"
                  onMouseEnter={() => setWorkOpen(true)}
                  onMouseLeave={() => setWorkOpen(false)}
                  onFocus={() => setWorkOpen(true)}
                  onBlur={(e) => {
                    if (!e.currentTarget.parentElement?.contains(e.relatedTarget)) {
                      setWorkOpen(false);
                    }
                  }}
                  aria-expanded={workOpen}
                  aria-haspopup="true"
                  aria-controls="work-menu"
                >
                  Our Work <ChevronDown className="w-4 h-4" aria-hidden="true" />
                </button>
                <div
                  id="work-menu"
                  className={`absolute top-full left-0 pt-2 ${workOpen ? "block" : "hidden"}`}
                  onMouseEnter={() => setWorkOpen(true)}
                  onMouseLeave={() => setWorkOpen(false)}
                  role="menu"
                  aria-label="Our Work submenu"
                >
                  <div className="bg-card rounded-xl shadow-xl border border-border py-2 min-w-[180px]">
                    {ourWork.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        className="block px-4 py-2.5 text-foreground hover:bg-muted hover:text-secondary transition-colors focus:outline-none focus-visible:bg-muted focus-visible:text-secondary"
                        role="menuitem"
                        onBlur={(e) => {
                          if (!e.currentTarget.parentElement?.contains(e.relatedTarget)) {
                            setWorkOpen(false);
                          }
                        }}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

              {/* About Dropdown */}
              <div className="relative group">
                <button
                  className="flex items-center gap-1 text-foreground hover:text-secondary transition-colors font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2 rounded-sm"
                  onMouseEnter={() => setAboutOpen(true)}
                  onMouseLeave={() => setAboutOpen(false)}
                  onFocus={() => setAboutOpen(true)}
                  onBlur={(e) => {
                    if (!e.currentTarget.parentElement?.contains(e.relatedTarget)) {
                      setAboutOpen(false);
                    }
                  }}
                  aria-expanded={aboutOpen}
                  aria-haspopup="true"
                  aria-controls="about-menu"
                >
                  About <ChevronDown className="w-4 h-4" aria-hidden="true" />
                </button>
                <div
                  id="about-menu"
                  className={`absolute top-full left-0 pt-2 ${aboutOpen ? "block" : "hidden"}`}
                  onMouseEnter={() => setAboutOpen(true)}
                  onMouseLeave={() => setAboutOpen(false)}
                  role="menu"
                  aria-label="About submenu"
                >
                  <div className="bg-card rounded-xl shadow-xl border border-border py-2 min-w-[180px]">
                    {aboutLinks.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        className="block px-4 py-2.5 text-foreground hover:bg-muted hover:text-secondary transition-colors focus:outline-none focus-visible:bg-muted focus-visible:text-secondary"
                        role="menuitem"
                        onBlur={(e) => {
                          if (!e.currentTarget.parentElement?.contains(e.relatedTarget)) {
                            setAboutOpen(false);
                          }
                        }}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

              <Link
                to="/blog"
                className={`text-foreground hover:text-secondary transition-colors font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2 rounded-sm ${
                  location.pathname.startsWith("/blog") ? "text-secondary" : ""
                }`}
              >
                Blog
              </Link>

              <Link
                to="/contact"
                className={`text-foreground hover:text-secondary transition-colors font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2 rounded-sm ${
                  location.pathname === "/contact" ? "text-secondary" : ""
                }`}
              >
                Contact
              </Link>
            </nav>

            {/* Phone & CTA */}
            <div className="hidden lg:flex items-center gap-4">
              <a
                href="tel:+17632457813"
                className="flex items-center gap-2 text-foreground font-bold text-lg hover:text-secondary transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2 rounded-sm"
                aria-label="Call Riekels Lawn Care at (763) 245-7813"
              >
                <Phone className="w-5 h-5" aria-hidden="true" />
                (763) 245-7813
              </a>
              <Link to="/contact">
                <Button className="bg-secondary hover:bg-secondary/90 text-secondary-foreground font-semibold px-6 focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2">
                  Get a Free Quote <ArrowRight className="w-4 h-4 ml-2" aria-hidden="true" />
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2 rounded-md"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-menu"
              aria-label={mobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" aria-hidden="true" /> : <Menu className="w-6 h-6" aria-hidden="true" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <nav 
              id="mobile-menu"
              className="lg:hidden py-4 border-t border-border max-h-[calc(100vh-180px)] overflow-y-auto"
              aria-label="Mobile navigation"
            >
              <div className="space-y-1">
                {/* Services Dropdown */}
                <button
                  className="flex items-center justify-between w-full px-4 py-3 text-foreground font-medium hover:bg-muted focus:outline-none focus-visible:bg-muted"
                  onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                  onKeyDown={(e) => handleKeyDown(e, () => setMobileServicesOpen(!mobileServicesOpen))}
                  aria-expanded={mobileServicesOpen}
                  aria-controls="mobile-services-menu"
                >
                  Services
                  <ChevronDown className={`w-5 h-5 transition-transform ${mobileServicesOpen ? "rotate-180" : ""}`} aria-hidden="true" />
                </button>
                {mobileServicesOpen && (
                  <div id="mobile-services-menu" className="bg-muted/50 py-1" role="menu">
                    {services.map((service) => (
                      <Link
                        key={service.path}
                        to={service.path}
                        className="block px-8 py-2.5 text-foreground hover:bg-muted text-sm focus:outline-none focus-visible:bg-muted"
                        onClick={() => setMobileMenuOpen(false)}
                        role="menuitem"
                      >
                        {service.name}
                      </Link>
                    ))}
                  </div>
                )}

                {/* Our Work Dropdown */}
                <button
                  className="flex items-center justify-between w-full px-4 py-3 text-foreground font-medium hover:bg-muted focus:outline-none focus-visible:bg-muted"
                  onClick={() => setMobileWorkOpen(!mobileWorkOpen)}
                  onKeyDown={(e) => handleKeyDown(e, () => setMobileWorkOpen(!mobileWorkOpen))}
                  aria-expanded={mobileWorkOpen}
                  aria-controls="mobile-work-menu"
                >
                  Our Work
                  <ChevronDown className={`w-5 h-5 transition-transform ${mobileWorkOpen ? "rotate-180" : ""}`} aria-hidden="true" />
                </button>
                {mobileWorkOpen && (
                  <div id="mobile-work-menu" className="bg-muted/50 py-1" role="menu">
                    {ourWork.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        className="block px-8 py-2.5 text-foreground hover:bg-muted text-sm focus:outline-none focus-visible:bg-muted"
                        onClick={() => setMobileMenuOpen(false)}
                        role="menuitem"
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                )}

                {/* About Dropdown */}
                <button
                  className="flex items-center justify-between w-full px-4 py-3 text-foreground font-medium hover:bg-muted focus:outline-none focus-visible:bg-muted"
                  onClick={() => setMobileAboutOpen(!mobileAboutOpen)}
                  onKeyDown={(e) => handleKeyDown(e, () => setMobileAboutOpen(!mobileAboutOpen))}
                  aria-expanded={mobileAboutOpen}
                  aria-controls="mobile-about-menu"
                >
                  About
                  <ChevronDown className={`w-5 h-5 transition-transform ${mobileAboutOpen ? "rotate-180" : ""}`} aria-hidden="true" />
                </button>
                {mobileAboutOpen && (
                  <div id="mobile-about-menu" className="bg-muted/50 py-1" role="menu">
                    {aboutLinks.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        className="block px-8 py-2.5 text-foreground hover:bg-muted text-sm focus:outline-none focus-visible:bg-muted"
                        onClick={() => setMobileMenuOpen(false)}
                        role="menuitem"
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                )}

                <Link
                  to="/blog"
                  className="block px-4 py-3 text-foreground hover:bg-muted font-medium focus:outline-none focus-visible:bg-muted"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Blog
                </Link>
                <Link
                  to="/contact"
                  className="block px-4 py-3 text-foreground hover:bg-muted font-medium focus:outline-none focus-visible:bg-muted"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Contact
                </Link>

                {/* Mobile CTA */}
                <div className="px-4 pt-4">
                  <Link to="/contact" onClick={() => setMobileMenuOpen(false)}>
                    <Button className="w-full bg-secondary hover:bg-secondary/90 text-secondary-foreground font-semibold">
                      Get a Free Quote <ArrowRight className="w-4 h-4 ml-2" aria-hidden="true" />
                    </Button>
                  </Link>
                </div>
              </div>
            </nav>
          )}
        </div>
      </div>
    </header>
  );
};
